import React, { useState, useRef, useEffect } from 'react';
import {
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Smartphone,
  CreditCard,
  Code2,
  Monitor,
  LogOut,
  ShieldCheck,
  CheckCircle2,
  Home,
  Database,
  Receipt,
  BarChart3,
  Wrench,
  PiggyBank,
  QrCode,
  Layers,
  Users,
  BookOpen,
} from 'lucide-react';

import { useNavigate, useLocation } from 'react-router-dom';

interface HeaderProps {
  appMode: 'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE';
  setAppMode: (mode: 'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE') => void;
  currentUser: any;
  onLogout: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  appMode,
  setAppMode,
  currentUser,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>('Transaksi');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleDropdown = (menu: string) => {
    setOpenDropdown((prev) => (prev === menu ? null : menu));
  };

  const selectItem = (path: string) => {
    navigate(path);
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection((prev) => (prev === section ? null : section));
  };

  const menuSections = [
    {
      name: 'Data Awal',
      icon: Database,
      items: [
        { label: 'Kelas', path: '/kelas' },
        { label: 'Siswa & Virtual Account', path: '/siswa' },
        { label: 'Tahun Ajaran', path: '/tahun-ajaran' },
        { label: 'Akun Keuangan (COA)', path: '/akun' },
        { label: 'Pos Tagihan', path: '/pos-tagihan' },
        { label: 'Tagihan Siswa', path: '/tagihan-siswa' },
        { label: 'Tagihan SPP Berkala (Otomatis)', path: '/tagihan-spp-berkala', badge: 'Auto' },
        { label: 'Setting Tahun Ajaran', path: '/setting' },
      ],
    },
    {
      name: 'Transaksi',
      icon: Receipt,
      items: [
        { label: 'Loket Pembayaran (POS & QRIS)', path: '/loket', badge: 'Utama' },
        { label: 'Cetak Bukti & Void Pembayaran', path: '/cetak-bukti' },
        { label: 'Saldo Tabungan Siswa', path: '/saldo-siswa' },
        { label: 'Log Transaksi Siswa', path: '/transaksi-siswa' },
      ],
    },
    {
      name: 'Report',
      icon: BarChart3,
      items: [
        { label: 'Laporan Rekap Tagihan', path: '/report/tagihan' },
        { label: 'Laporan Pembayaran Siswa', path: '/report/pembayaran' },
        { label: 'Laporan Tunggakan Siswa', path: '/report/tunggakan', badge: 'Penting' },
        { label: 'Laporan Penerimaan Kas / Bank', path: '/report/penerimaan' },
      ],
    },
    {
      name: 'Utility',
      icon: Wrench,
      items: [
        { label: 'Proses Naik Kelas', path: '/utility/naik-kelas' },
        { label: 'Proses Kelulusan Siswa', path: '/utility/kelulusan' },
        { label: 'User Privilege (Hak Akses)', path: '/utility/user-privilege' },
        { label: 'Activity Log (Audit Trail)', path: '/utility/activity-log' },
        { label: 'Backup & Restore Database', path: '/utility/backup' },
      ],
    },
    {
      name: 'Tabungan',
      icon: PiggyBank,
      items: [
        { label: 'Data Mutasi Tabungan', path: '/tabungan/mutasi' },
        { label: 'Laporan Buku Tabungan', path: '/report/tabungan' },
      ],
    },
    {
      name: 'Buku Panduan',
      icon: BookOpen,
      items: [
        { label: 'Buku Panduan & Docs', path: '/panduan', badge: 'Docs' },
      ],
    },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm select-none">
      {/* Top Header Bar */}
      <div className="bg-gradient-to-r from-sky-800 via-sky-700 to-teal-800 text-white px-3 sm:px-4 py-2 sm:py-2.5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          {/* Brand Logo & Name */}
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shadow-inner shrink-0">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-amber-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="font-black tracking-wider text-base sm:text-xl text-white">SDMT</span>
                <span className="text-[10px] sm:text-xs bg-amber-400 text-sky-950 font-black px-1.5 py-0.5 rounded shrink-0">
                  PONOROGO
                </span>
                <span className="text-[11px] text-sky-100 hidden lg:inline-block border-l border-white/25 pl-2 truncate">
                  Sekolah Dasar Muhammadiyah Terpadu
                </span>
              </div>
              <p className="text-[10px] sm:text-[11px] text-sky-100/90 font-medium truncate hidden xs:block">
                Sistem Pembayaran Terpadu (QRIS, Duitku & Midtrans Gateway)
              </p>
            </div>
          </div>


          {/* Mode Switcher Return Button (only shows when NOT in WEB_PORTAL) */}
          {appMode !== 'WEB_PORTAL' && (
            <div className="hidden xl:flex items-center gap-1 bg-black/25 p-1 rounded-lg border border-white/15 backdrop-blur-sm shrink-0">
              <button
                onClick={() => setAppMode('WEB_PORTAL')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer bg-white text-sky-900 shadow-sm"
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Kembali ke Portal Admin</span>
              </button>
            </div>
          )}

          {/* Right Area: User Avatar & Mobile Hamburger Button */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Panduan Docs Quick Button */}
            <button
              onClick={() => navigate('/panduan')}
              title="Buku Panduan & Dokumentasi Resmi"
              className="flex items-center gap-1.5 px-2 py-1.5 sm:px-2.5 sm:py-1.5 text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition cursor-pointer text-xs font-semibold shadow-xs"
            >
              <BookOpen className="w-3.5 h-3.5 text-amber-300" />
              <span className="hidden sm:inline">Panduan</span>
            </button>

            {/* User Profile info */}
            {/* User Info (desktop & mobile) */}
            <div className="flex items-center gap-1.5 sm:gap-2 text-right">
              <div className="hidden sm:block">
                <p className="text-xs font-bold leading-tight truncate max-w-[120px]">
                  {currentUser?.nama || 'Petugas Loket'}
                </p>
                <p className="text-[10px] text-sky-200 truncate">{currentUser?.levelUser || 'Admin Keuangan'}</p>
              </div>
              <div className="w-7 h-7 rounded-full bg-sky-200 text-sky-900 font-bold text-xs flex items-center justify-center shrink-0">
                {currentUser?.nama?.charAt(0) || 'A'}
              </div>
            </div>

            {/* Logout button (visible on both mobile and desktop) */}
            <button
              id="btn-logout"
              onClick={onLogout}
              title="Logout / Keluar"
              className="flex items-center gap-1 px-2 py-1.5 sm:px-2.5 sm:py-1.5 text-white/95 hover:text-white bg-rose-500/25 hover:bg-rose-600/40 border border-rose-400/40 rounded-lg transition cursor-pointer text-xs font-semibold shadow-xs"
            >
              <LogOut className="w-3.5 h-3.5 text-rose-200" />
              <span className="hidden sm:inline">Logout</span>
            </button>

            {/* Mobile / Responsive Hamburger Toggle */}
            <button
              id="btn-mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
              className="flex items-center justify-center p-2 rounded-lg bg-white/15 hover:bg-white/25 active:bg-white/30 text-white transition cursor-pointer border border-white/20"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <div className="flex items-center gap-1.5">
                  <Menu className="w-5 h-5 text-white" />
                  <span className="text-xs font-bold md:hidden">Menu</span>
                </div>
              )}
            </button>
          </div>
        </div>


      </div>

      {/* Desktop Main Navigation Bar removed - now handled by Sidebar.tsx */}

      {/* Mobile Drawer / Full Screen Responsive Navigation */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 top-[52px] sm:top-[56px] z-40 bg-slate-900/60 backdrop-blur-xs flex flex-col justify-start animate-in fade-in duration-150">
          <div className="bg-white text-slate-900 shadow-2xl border-b border-slate-300 max-h-[85vh] overflow-y-auto flex flex-col">


            {/* Mobile Navigation Links */}
            <div className="p-3 space-y-1.5 flex-1">
              <div className="pb-1.5 flex items-center justify-between border-b border-slate-100">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Menu Administrasi Sekolah
                </span>
                <span className="text-[10px] text-sky-700 font-semibold bg-sky-50 px-2 py-0.5 rounded">
                  SDMT Ponorogo
                </span>
              </div>

              {/* Home Item */}
              <button
                onClick={() => selectItem('/')}
                className={`w-full p-2.5 rounded-lg flex items-center justify-between font-semibold text-xs transition ${
                  currentPath === '/'
                    ? 'bg-sky-600 text-white font-bold'
                    : 'hover:bg-slate-100 text-slate-800'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Home className="w-4 h-4" />
                  <span>Dashboard Home</span>
                </div>
                {currentPath === '/' && <CheckCircle2 className="w-4 h-4 text-white" />}
              </button>

              {/* Accordion Menu Sections */}
              {menuSections.map((sec) => {
                const isExpanded = mobileExpandedSection === sec.name;
                const hasActiveSub = sec.items.some((it) => it.path === currentPath);

                return (
                  <div key={sec.name} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleMobileSection(sec.name)}
                      className={`w-full p-2.5 flex items-center justify-between font-bold text-xs transition cursor-pointer ${
                        hasActiveSub ? 'bg-sky-50 text-sky-900' : 'bg-slate-50 text-slate-800 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <sec.icon className={`w-4 h-4 ${hasActiveSub ? 'text-sky-600' : 'text-slate-500'}`} />
                        <span>{sec.name}</span>
                        {hasActiveSub && (
                          <span className="w-2 h-2 rounded-full bg-sky-600 inline-block"></span>
                        )}
                      </div>
                      <ChevronRight
                        className={`w-4 h-4 text-slate-400 transition-transform duration-150 ${
                          isExpanded ? 'rotate-90 text-sky-600' : ''
                        }`}
                      />
                    </button>

                    {/* Sub-items */}
                    {isExpanded && (
                      <div className="p-1.5 bg-white divide-y divide-slate-100 animate-in fade-in">
                        {sec.items.map((it) => {
                          const isSubActive = currentPath === it.path;
                          return (
                            <button
                              key={it.path}
                              onClick={() => selectItem(it.path)}
                              className={`w-full p-2 rounded text-left text-xs flex items-center justify-between transition cursor-pointer ${
                                isSubActive
                                  ? 'bg-sky-600 text-white font-bold'
                                  : 'hover:bg-slate-50 text-slate-700'
                              }`}
                            >
                              <span>{it.label}</span>
                              {it.badge && (
                                <span
                                  className={`text-[9px] font-black px-1.5 py-0.5 rounded ${
                                    isSubActive
                                      ? 'bg-white text-sky-900'
                                      : it.badge === 'Utama'
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-rose-100 text-rose-800'
                                  }`}
                                >
                                  {it.badge}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Gateway direct item */}
              <button
                onClick={() => selectItem('/payment-gateway')}
                className={`w-full p-2.5 rounded-lg flex items-center justify-between font-semibold text-xs transition border border-emerald-200 ${
                  currentPath === '/payment-gateway'
                    ? 'bg-emerald-600 text-white font-bold'
                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-900'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <CreditCard className="w-4 h-4 text-emerald-600" />
                  <span>WhatsApp Gateway Console</span>
                </div>
                <span className="text-[9px] bg-emerald-200 text-emerald-900 font-bold px-1.5 py-0.5 rounded">
                  Live
                </span>
              </button>

              {/* Direct Logout in Mobile Menu */}
              <button
                id="btn-mobile-drawer-logout"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onLogout();
                }}
                className="w-full p-2.5 rounded-lg flex items-center justify-between font-bold text-xs transition border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 cursor-pointer shadow-xs mt-3"
              >
                <div className="flex items-center gap-2.5">
                  <LogOut className="w-4 h-4 text-rose-600" />
                  <span>Logout / Keluar Akun</span>
                </div>
                <span className="text-[10px] bg-rose-200 text-rose-900 font-extrabold px-2 py-0.5 rounded">
                  Keluar
                </span>
              </button>
            </div>

            {/* Mobile Footer with User, Logout & Close */}
            <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-sky-200 text-sky-900 font-bold flex items-center justify-center shrink-0">
                  {currentUser?.nama?.charAt(0) || 'A'}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 leading-tight truncate">{currentUser?.nama || 'Petugas SDMT'}</p>
                  <p className="text-[10px] text-slate-500 truncate">{currentUser?.levelUser || 'Admin'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  id="btn-mobile-footer-logout"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white rounded-md font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Logout</span>
                </button>

                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-2.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-md font-semibold text-xs transition cursor-pointer"
                >
                  Tutup Menu
                </button>
              </div>
            </div>
          </div>

          {/* Dimmed backdrop area (click to close) */}
          <div
            className="flex-1 w-full"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        </div>
      )}
    </header>
  );
};
